import React from 'react';
import xor from 'lodash/xor';
import Select, {type ActionMeta, type Props as ReactSelectProps} from 'react-select';
import {useTranslation} from 'react-i18next';
import {EzTextStyle} from '@ezcater/recipe';
import {SelectLabel, ErrorText} from './StoresSelect.styles';
import colors from '../../../styles/colors';
import {fontSizes} from '../../../styles/typography';

export const SELECT_ALL_OPTION_VALUE = 'all';

type SelectOption = {
  isFixed?: boolean;
  label: string;
  value: string;
};

type Store = {
  address: {street: string} | null;
  isCurrent?: boolean;
  name: string;
  uuid: string;
};

const getOptionsMap = (stores: Store[] = []): Record<string, SelectOption> => {
  return stores.reduce<Record<string, SelectOption>>((acc, store) => {
    acc[store.uuid] = {
      isFixed: store.isCurrent,
      label: [store.name?.trim(), store.address?.street?.trim()].filter(Boolean).join(' ') || '',
      value: store.uuid,
    };

    return acc;
  }, {});
};

type SelectProps = ReactSelectProps<SelectOption, true>;

type StoresSelectProps = Omit<SelectProps, 'onChange' | 'value' | 'isMulti'> & {
  error?: string | string[];
  onChange: (options: string[], actionMeta: ActionMeta<SelectOption>) => void;
  stores: Store[];
  touched?: boolean;
  value?: string[];
  helperText?: string;
};

const StoresSelect = ({
  error,
  inputId = 'stores',
  onChange,
  stores = [],
  styles: overrideStyles,
  touched,
  value,
  helperText,
  ...selectProps
}: StoresSelectProps) => {
  const [t] = useTranslation();

  const selectAllOption = {
    label: t('components.deliveryManagement.storesSelect.selectAllLabel'),
    value: SELECT_ALL_OPTION_VALUE,
    isFixed: false,
  };
  const optionsMap = getOptionsMap(stores);
  const options = [selectAllOption, ...Object.values(optionsMap)];
  const errors = (Array.isArray(error) ? error : [error]).filter(Boolean);
  const hasError = !!errors.length;

  const styles: SelectProps['styles'] = {
    multiValue: (base, state) => {
      return state.data.isFixed ? {...base, backgroundColor: 'gray'} : base;
    },
    multiValueLabel: (base, state) => {
      return state.data.isFixed
        ? {...base, fontWeight: 'bold', color: 'white', paddingRight: 6}
        : base;
    },
    multiValueRemove: (base, state) => {
      return state.data.isFixed ? {...base, display: 'none'} : base;
    },
    control: base => {
      return {...base, borderColor: hasError ? colors.red : base.borderColor};
    },
    ...overrideStyles,
  };

  const handleChange: SelectProps['onChange'] = (nextValue, actionMeta) => {
    if (actionMeta.action === 'remove-value' || actionMeta.action === 'pop-value') {
      return onChange(
        nextValue.map(o => o.value).filter(v => v !== actionMeta.removedValue.value),
        actionMeta
      );
    }

    if (actionMeta.action === 'clear') {
      return onChange(
        nextValue.filter(o => o.isFixed).map(o => o.value),
        actionMeta
      );
    }

    if (nextValue.find(o => o.value === SELECT_ALL_OPTION_VALUE)) {
      return onChange([SELECT_ALL_OPTION_VALUE], actionMeta);
    }

    return onChange(
      nextValue.map(o => o.value),
      actionMeta
    );
  };

  const getSelectedValues = () => {
    const fixedValues = options.filter(o => o.isFixed).map(o => o.value);
    const nonFixedValues = (value || []).filter(v => !fixedValues.includes(v));
    const allValues = fixedValues.concat(nonFixedValues);
    const allStoreIds = stores.map(s => s.uuid);

    if (stores.length > 3 && !xor(allStoreIds, allValues)?.length) {
      return [selectAllOption];
    }

    return allValues.map(v => optionsMap[v]).filter(Boolean);
  };

  const selectedValues = getSelectedValues();

  return (
    <>
      <SelectLabel hasError={hasError}>
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label htmlFor={inputId}>Store</label>
      </SelectLabel>
      {helperText && (
        <EzTextStyle use="subdued" style={{fontSize: fontSizes.xsmall}}>
          {helperText}
        </EzTextStyle>
      )}
      <Select
        aria-invalid={hasError}
        inputId={inputId}
        isClearable={selectedValues.some(o => !o.isFixed)}
        isMulti
        onChange={handleChange}
        options={options}
        placeholder={t('components.deliveryManagement.storesSelect.placeholder')}
        styles={styles}
        value={selectedValues}
        {...selectProps}
      />
      {!!touched && hasError && (
        <ErrorText>
          {errors.map((errorMessage, index) => (
            <div key={index}>
              <EzTextStyle>{errorMessage}</EzTextStyle>
            </div>
          ))}
        </ErrorText>
      )}
    </>
  );
};

export default StoresSelect;
